import { useState } from "react";
import sumBy from "lodash/sumBy";
import db from "../../../../database";
import { getNewOmsOrder, updateOmsOrder } from "../../../../services";

// OMS Orders Modal Imports
import NewWhite from "../../../../assets/images/pending.svg";
import PreparingWhite from "../../../../assets/images/parkedOrder.svg";
import ReadyWhite from "../../../../assets/images/completed.svg";
import CompletedWhite from "../../../../assets/images/prepared.svg";
import New from "../../../../assets/images/new.svg";
import Preparing from "../../../../assets/images/preparing.svg";
import Ready from "../../../../assets/images/ready.svg";
import Completed from "../../../../assets/images/todayOrders.svg";

/**
 * useOMSOperations
 *
 * Encapsulates OMS (Order Management System) order handling:
 * fetching, status updates, searching, and selection.
 */
const useOMSOperations = ({
  tillData,
  setSideMenuDrawervisible,
}) => {
  // ── Static data ────────────────────────────────────────────────────────
  const omsOrderStatus = [
    {
      title: "New",
      imgSrc: NewWhite,
      selectedImgSrc: New,
      statusValue: "NE",
    },
    {
      title: "Preparing",
      imgSrc: PreparingWhite,
      selectedImgSrc: Preparing,
      statusValue: "UP",
    },
    {
      title: "Ready",
      imgSrc: ReadyWhite,
      selectedImgSrc: Ready,
      statusValue: "PK",
    },
    {
      title: "Today's Orders",
      imgSrc: CompletedWhite,
      selectedImgSrc: Completed,
      statusValue: "DE",
    },
  ];

  // ── State ──────────────────────────────────────────────────────────────
  const omsOrderDetailsList = JSON.parse(localStorage.getItem("omsOrderDetails"));
  const initialOmsOrderList = omsOrderDetailsList ? omsOrderDetailsList : [];
  const [omsOrdersList, setOmsOrderList] = useState(initialOmsOrderList);

  const [displayOMSOrderItemsModal, setDisplayOMSOrderItemsModal] = useState(false);
  const [selectedOMSOrderStatus, setSelectedOMSOrderStatus] = useState(omsOrderStatus[0]);

  const [order, setOrder] = useState("");
  const [searchOrders, setSearchOrders] = useState(omsOrdersList);

  const [selectedOrder, setSelectedOrder] = useState({});

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleOmsOrders = async () => {
    setSideMenuDrawervisible(false);
    db.orders
      .orderBy("orderTime")
      .limit(20)
      .reverse()
      .toArray()
      .then((data) => {
        if (data?.length > 0) {
          data?.map((item) => {
            let time = new Date(item.orderTime);
            let newTime = time.toLocaleString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            });
            let newLineItems = item?.items?.map((itemLine) => ({
              ...itemLine,
              price: itemLine?.salePrice || 0,
              quantity: itemLine?.weight || 0,
            }));
            let orderObj = {
              cWCOrderId: item.sOrderID || "",
              customerId: item?.customer?.cwrCustomerId || "",
              customerName: item?.customer?.name || "",
              lineItems: [...newLineItems],
              mobileNo: item?.customer?.mobileNo,
              noOfItems: item?.items?.length,
              orderNo: item.documentno,
              posOrders: "Y",
              status: "NE",
              total: item?.total || 0,
              totalQty: item?.totalQty || 0,
              dateCreated: newTime,
            };
            let findIndexOrder = omsOrdersList.findIndex((o) => o.cWCOrderId === orderObj.cWCOrderId);
            if (findIndexOrder === -1) {
              omsOrdersList.push(orderObj);
            } else {
              omsOrdersList[findIndexOrder] = orderObj;
            }
            return null;
          });
        }
      });
    const paramsInput = {
      query: `query{
          getNewOmsOrders(bunitId:"${tillData.tillAccess.csBunit.csBunitId}"){
          cWCOrderId
          orderNo
          dateCreated
          customerId
          customerName
          mobileNo
          total
          noOfItems
          totalQty
          lineItems{
              productId
              name
              productCode
              quantity
              price
              subTotal
              addOnProducts{
                  id
                  name
                  price
              }
          }
      }
      }`,
    };
    try {
      const response = await getNewOmsOrder(paramsInput);

      const { getNewOmsOrders } = response?.data?.data || {};
      if (getNewOmsOrders) {
        const updatedOmsOrders = [...omsOrdersList];

        for (const item of getNewOmsOrders) {
          const obj = {
            ...item,
            status: "NE",
            noOfItems: item.lineItems.length,
            posOrders: "N",
            dateCreated: new Date(item.dateCreated).toLocaleString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            }),
          };

          const findIndex = updatedOmsOrders.findIndex((o) => o.cWCOrderId === item.cWCOrderId);
          if (findIndex === -1) {
            updatedOmsOrders.push(obj);
          } else {
            updatedOmsOrders[findIndex] = obj;

            const prodCodes = (updatedOmsOrders[findIndex]?.lineItems || []).map((li) => li.productCode);
            const allMatchingProducts = await db.products
              .where("value")
              .startsWithAnyOfIgnoreCase(prodCodes)
              .toArray();
            const productsFetched = allMatchingProducts.filter(
              (product) => product.isactive == true && product.productSegment !== "BG"
            );

            if (productsFetched?.length > 0) {
              const newLineItems = updatedOmsOrders[findIndex].lineItems.map((lineItem) => {
                const product = productsFetched.find((product) => product.value === lineItem.productCode);
                return product ? { ...lineItem, img: product.imageurl } : { ...lineItem };
              });
              updatedOmsOrders[findIndex].lineItems = newLineItems;
            }
          }
        }

        // Update localStorage and state with the updated orders
        localStorage.setItem("omsOrderDetails", JSON.stringify(updatedOmsOrders));
        setOmsOrderList(updatedOmsOrders);
      }
    } catch (error) {
      console.error("Error processing OMS orders:", error);
    }

    setDisplayOMSOrderItemsModal(true);
  };

  const handleOMSOrderStatusSelection = (record) => {
    setSelectedOMSOrderStatus(record);
  };

  const setOMSStatus = (status) => {
    let newOMSStatus = "";
    switch (status) {
      case "NE":
        newOMSStatus = "UP";
        break;
      case "UP":
        newOMSStatus = "PK";
        break;
      case "PK":
        newOMSStatus = "DE";
        break;
      default:
        newOMSStatus = "NE";
    }
    return newOMSStatus;
  };

  const nextOMSOrderStatus = () => {
    let newOMSOrderStatus = [...omsOrderStatus];
    let statusIndex = newOMSOrderStatus.findIndex((item) => item.title === selectedOMSOrderStatus.title);
    if (statusIndex <= newOMSOrderStatus.length - 1) setSelectedOMSOrderStatus(newOMSOrderStatus[statusIndex + 1]);
  };

  const handleOMSStatusButton = async (record) => {
    const paramsInput = {
      query: `mutation{
          updateOMSOrderStatus(order:{
              cWCOrderId: "${record.cWCOrderId}"
              status: "${setOMSStatus(record.status)}"
          })
          {
              status
              message
          }
      }`,
    };
    try {
      const response = await updateOmsOrder(paramsInput);

      if (response?.data?.data?.updateOMSOrderStatus?.status === "200") {
        const newOMSOrderDetails = omsOrdersList.map((order) => (order.cWCOrderId === record.cWCOrderId ? { ...order, status: setOMSStatus(record.status) } : order));

        localStorage.setItem("omsOrderDetails", JSON.stringify(newOMSOrderDetails));
        setOmsOrderList(newOMSOrderDetails);
        nextOMSOrderStatus();
      } else {
        console.error("Failed to update OMS order status:", response?.data?.data);
      }
    } catch (error) {
      console.error("Error in updating OMS order status:", error);
    }
  };

  const handleOrderCard = (record) => {
    setSelectedOrder(record);
    handleOMSOrderStatusSelection(omsOrderStatus.filter((statusItem) => statusItem.statusValue === record.status)?.[0]);
  };

  const handleOrderSearchInput = (value) => {
    if (value !== "") {
      const results = omsOrdersList.filter((orderDetails) => {
        return orderDetails.orderNo.startsWith(value);
      });
      setSearchOrders(results);
    } else {
      setSearchOrders(omsOrdersList);
    }
    setOrder(value);
  };

  const omsOrderTotalPrice = sumBy(selectedOrder.lineItems, "price");

  // ── Return ────────────────────────────────────────────────────────────

  return {
    // State
    omsOrdersList,
    setOmsOrderList,
    displayOMSOrderItemsModal,
    setDisplayOMSOrderItemsModal,
    selectedOMSOrderStatus,
    setSelectedOMSOrderStatus,
    order,
    setOrder,
    searchOrders,
    setSearchOrders,
    selectedOrder,
    setSelectedOrder,
    omsOrderStatus,
    omsOrderTotalPrice,

    // Functions
    handleOmsOrders,
    handleOMSOrderStatusSelection,
    setOMSStatus,
    handleOMSStatusButton,
    handleOrderCard,
    handleOrderSearchInput,
  };
};

export default useOMSOperations;
